import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Clock } from 'lucide-react';
import { useRankLater } from '@/hooks/useRankLater';
import { PairwiseSheet } from './PairwiseSheet';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function RankLaterQueue() {
  const { skippedBooks, loading, markBookAsRanked } = useRankLater();
  const [rankingBook, setRankingBook] = useState<any>(null);
  const [pairwiseData, setPairwiseData] = useState<any>(null);
  const [startingId, setStartingId] = useState<string | null>(null);

  const handleStartRanking = async (book: any) => {
    try {
      if (startingId) return; // prevent parallel starts
      setStartingId(book.book_id);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error('Please sign in to continue');
        return;
      }

      // Trigger placement algorithm to start ranking
      const { data, error } = await supabase.functions.invoke('placement-algorithm', {
        body: {
          userId: userData.user.id,
          bookId: book.book_id,
          tier: book.tier
        }
      });

      if (error) {
        console.error('Error from placement-algorithm:', error);
        throw error;
      }

      console.log('Placement algorithm response:', data);

      // Check if we need to show comparisons
      if (data.comparison && data.state) {
        setPairwiseData({
          placementState: data.state,
          comparison: data.comparison
        });
        setRankingBook(book);
      } else if (data.phase === 'complete' && data.finalPosition) {
        toast.success(`Book placed at position ${data.finalPosition}!`);
        window.dispatchEvent(new CustomEvent('rankings-updated'));
        markBookAsRanked(book.book_id);
      }
    } catch (error) {
      console.error('Error starting ranking:', error);
      toast.error('Failed to start ranking');
    } finally {
      setStartingId(null);
    }
  };

  const handlePairwiseComplete = () => {
    window.dispatchEvent(new CustomEvent('rankings-updated'));
    if (rankingBook) {
      markBookAsRanked(rankingBook.book_id);
    }
    setRankingBook(null);
    setPairwiseData(null);
  };

  if (loading || skippedBooks.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Rank Later
            <Badge variant="secondary" className="ml-auto">
              {skippedBooks.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Complete these rankings to improve your leaderboard accuracy
          </p>
          <div className="space-y-2">
            {skippedBooks.slice(0, 5).map((book) => (
              <div
                key={book.book_id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                {book.cover_url && (
                  <img
                    src={book.cover_url}
                    alt={book.title}
                    className="w-12 h-16 object-cover rounded flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold line-clamp-1">{book.title}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {book.authors.join(', ')}
                  </p>
                  <Badge variant="secondary" className="text-xs mt-1">
                    {book.tier}★
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStartRanking(book)}
                >
                  Rank Now
                </Button>
              </div>
            ))}
          </div>
          {skippedBooks.length > 5 && (
            <p className="text-sm text-muted-foreground text-center mt-4">
              +{skippedBooks.length - 5} more books to rank
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pairwise comparison sheet */}
      {rankingBook && pairwiseData && (
        <PairwiseSheet
          isOpen={!!rankingBook}
          onClose={() => {
            setRankingBook(null);
            setPairwiseData(null);
          }}
          currentBook={{
            id: rankingBook.book_id,
            title: rankingBook.title,
            authors: rankingBook.authors,
            cover_url: rankingBook.cover_url
          }}
          tier={rankingBook.tier}
          candidates={[]}
          onComplete={handlePairwiseComplete}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
        />
      )}
    </>
  );
}
